import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, User, Crown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { TextSizeControl } from "./TextSizeControl";
import { NotificationBell } from "./NotificationBell";

const LINKS = [
  { label: "Home", href: "#home" },
  { label: "Schedule", href: "#schedule" },
  { label: "Pricing", href: "#pricing" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  useEffect(() => { if (user?.role === "admin") import("../pages/Admin"); }, [user]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-black/80 backdrop-blur-md border-b border-neutral-900" : "bg-transparent"
      }`}
    >
      <nav aria-label="Main navigation" className="mx-auto max-w-[1400px] px-5 sm:px-8 h-16 flex items-center justify-between">
        <a href="#home" data-testid="nav-logo" aria-label="Sculpt Fusion Pilates — home" className="font-script text-3xl neon-text leading-none">
          Sculpt Fusion
        </a>

        <ul className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <li key={l.href}>
              <a
                href={l.href}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className="text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-white transition-colors duration-200"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden md:flex items-center gap-3">
          <TextSizeControl />
          <NotificationBell />
          {user ? (
            <Link
              to={user.role === "admin" ? "/admin" : "/account"}
              data-testid="nav-account"
              className="inline-flex items-center gap-2 border border-neutral-700 text-white text-xs uppercase tracking-[0.2em] px-5 py-3 hover:border-neon hover:text-neon transition-colors duration-200"
            >
              {user.tier === "vip" ? <Crown size={14} className="text-neon" /> : <User size={14} />}
              {user.role === "admin" ? "Admin" : "Account"}
            </Link>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="inline-flex items-center gap-2 text-white text-xs uppercase tracking-[0.2em] px-3 py-3 hover:text-neon transition-colors duration-200"
            >
              <User size={14} /> Sign in
            </Link>
          )}
          <a
            href="#schedule"
            data-testid="nav-book-cta"
            className="inline-flex items-center bg-neon text-black font-semibold text-xs uppercase tracking-[0.2em] px-6 py-3 hover:bg-white hover:text-black transition-colors duration-200"
          >
            Book a Mat
          </a>
        </div>

        <div className="md:hidden flex items-center gap-3">
          <NotificationBell compact />
          <button
            data-testid="nav-mobile-toggle"
            className="inline-flex flex-col items-center justify-center gap-0.5 h-12 min-w-[3.25rem] px-2 border border-neutral-700 text-white hover:border-neon hover:text-neon active:bg-neon active:text-black transition-colors touch-manipulation"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <X size={26} strokeWidth={2.5} /> : <Menu size={28} strokeWidth={2.5} />}
            <span className="text-[8px] uppercase tracking-[0.2em] leading-none">{open ? "Close" : "Menu"}</span>
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black border-b border-neutral-900"
          >
            <ul className="flex flex-col px-6 py-3">
              {LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                    className="block py-4 text-base uppercase tracking-[0.2em] text-neutral-200 border-b border-neutral-900 hover:text-neon active:text-neon transition-colors touch-manipulation"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li>
                <Link
                  to={user ? (user.role === "admin" ? "/admin" : "/account") : "/login"}
                  onClick={() => setOpen(false)}
                  data-testid="nav-mobile-account"
                  className="block py-4 text-base uppercase tracking-[0.2em] text-neon font-semibold touch-manipulation"
                >
                  {user ? (user.role === "admin" ? "Admin" : "Account") : "Sign in"}
                </Link>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
